import {parseAppZoneDate, formatDHM} from '@/utils/dateUtils'

// ── Translation pass-through (phase-1 stub for plugin's hopnLanguageArray) ──
// Plugin uses hopnLanguageArray('section', 'key', 'fallback English text') for i18n.
// We don't have an i18n system here yet, so we just return the fallback string.
// Real translation can be wired up later as a separate task.
export const hopnLanguageArray = (_section, _key, fallback) => fallback

// ── Policies listing helper (verbatim from plugin includes/functions.js) ──
export function getHopnPolicies() {
  // Four rows, not six: the passenger and driver lateness pages are now the two
  // tabs of ONE component (latenessPolicies.jsx), and Refund is a tab of the CMR
  // popup — so each pair collapses into a single row. The steps behind the dropped
  // rows still exist; they're just no longer separate entries here.
  return [
    {title: 'Cancellation, Change & Refund', image_name: 'cancel-chnage.svg', page_name: 'cancellationPolicies'},
    {title: 'Guaranteed pickup', image_name: 'good-shild.svg', page_name: 'pickupPolicies'},
    {title: 'Passenger / Driver late arrival, no show', image_name: 'performance.svg', page_name: 'passengerPolicies'},
    {title: 'Toll Charges', image_name: 'doller-clickl.svg', page_name: 'tollPolicies'},
  ];
}

// ── Location formatters ──

export function locationLabel(loc) {
    if (!loc) return ''
    if (typeof loc === 'string') return loc
    return loc.address || [loc.city, loc.state, loc.country].filter(Boolean).join(', ') || ''
}

export function locationCityShort(loc) {
    if (!loc) return ''
    if (typeof loc === 'string') return loc
    const parts = [loc.city, loc.state].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : (loc.address || '')
}

// Returns only the address field (no city/state fallback). Used where empty should stay empty
// (e.g. autocomplete inputs that must reflect the actual cleared value).
export function locationAddress(loc) {
    if (!loc) return ''
    if (typeof loc === 'string') return loc
    return loc.address || ''
}

// Compares two locations as equal within ~1m tolerance. Used to avoid false "dirty" detection
// when Google Places returns slightly different decimal precision than stored lat/lng.
export function sameLoc(a, b) {
    const ea = !a?.address, eb = !b?.address
    if (ea && eb) return true
    if (ea !== eb) return false
    return Math.abs(Number(a.lat) - Number(b.lat)) < 1e-5 && Math.abs(Number(a.lng) - Number(b.lng)) < 1e-5
}

// ── Date / time formatters ──
//
// DISPLAY runs in the VIEWER's timezone; INPUT stays in APP_TIMEZONE. The two are
// deliberately different frames:
//   - The booking picker offers — and submits — Toronto wall-clock times, because the
//     passenger is choosing a slot for a Toronto pickup (see tripNow()). Unchanged.
//   - Everything READ BACK from the server is rendered in the visitor's own zone, so an
//     absolute time can never contradict a countdown sitting next to it. That's what was
//     broken: `cancellation_policy.hours_until_pickup` is computed server-side on real
//     instants ("Pickup in 46m"), while the tier cut-off was echoed as raw Toronto
//     wall-clock ("Until 7:00 AM today") — nine timezones away those read as a
//     contradiction, and the deadline looked long past.
//
// The mechanics: server timestamps arrive naive ("2026-08-12 07:00:00"), which is
// APP_TIMEZONE wall-clock, NOT UTC. Plain `new Date(str)` reads that as the VISITOR's
// local time and then `toLocale*` formats it back in the same zone — the two errors
// cancel and the Toronto wall clock is echoed verbatim, unconverted. parseAppZoneDate
// resolves the string to the real instant instead; `toLocale*` with NO timeZone option
// then renders that instant wherever the viewer is.
//
// parseAppZoneDate's other two branches matter as much as the naive one:
//   - a Date passed straight in is returned as-is, so booking-flow values built from
//     tripNow() (wall-clock SHIFTED, epoch intentionally wrong) still read back as
//     Toronto — the picker keeps working untouched;
//   - a string that already carries a zone (…Z or ±HH:MM) is a real instant already and
//     passes through, so AeroDataBox flight times behave exactly as before.

export function formatTime(dateStr) {
    if (!dateStr) return '--'
    const d = parseAppZoneDate(dateStr)
    if (!d || isNaN(d.getTime())) return '--'
    return d.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit', hour12: true})
}

export function formatDateTime(dateStr) {
    if (!dateStr) return '--'
    const d = parseAppZoneDate(dateStr)
    if (!d || isNaN(d.getTime())) return '--'
    return d.toLocaleDateString('en-US', {weekday: 'long', month: 'short', day: 'numeric'}) +
        ', ' + d.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit', hour12: true})
}

export function formatShortDate(dateStr) {
    if (!dateStr) return ''
    const d = parseAppZoneDate(dateStr)
    if (!d || isNaN(d.getTime())) return ''
    return d.toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'})
}

// Label + the portal-wide d/h/m duration (see formatDHM).
export function formatTravelTime(minutes) {
    if (!minutes) return ''
    return `Travel time: ${formatDHM(minutes)}`
}

export function formatTimeAgo(dateStr) {
    if (!dateStr) return ''
    const now = new Date()
    // Naive server timestamps ("2026-07-20 06:15:00") are APP_TIMEZONE wall-clock, not
    // UTC — parseAppZoneDate resolves them to the real instant, so the elapsed time is
    // right whatever zone the visitor is in.
    const date = parseAppZoneDate(dateStr)
    if (!date || isNaN(date)) return ''
    const diffMs = now - date
    const diffMin = Math.floor(diffMs / 60000)
    const diffHr = Math.floor(diffMs / 3600000)
    const diffDay = Math.floor(diffMs / 86400000)

    if (diffMin < 1) return 'Just now'
    if (diffMin < 60) return `${diffMin} min ago`
    if (diffHr < 24) return `${diffHr} hr ago`
    // The VISITOR's weekday/date, like every other rendered time in this file. These two
    // lines were the odd ones out: they pinned to APP_TIMEZONE while the "3 hr ago" branch
    // directly above them is measured against the visitor's own now, so an item could read
    // "Mon" beside a list of times printed on a clock where it was already Tuesday.
    if (diffDay < 7) return date.toLocaleDateString('en-US', {weekday: 'short'})
    return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric'})
}

// ── Price formatter ──
// Server stores prices in cents. `decimals` controls display precision ($32 vs $32.00).
export function formatPrice(cents, {decimals = 0} = {}) {
    if (cents === null || cents === undefined) return '--'
    return `$${(cents / 100).toFixed(decimals)}`
}

export function formatTripPrice(trip, opts) {
    return formatPrice(trip?.selected_vehicle?.total_price, opts)
}

// ── Trip status helpers ──

export function getRideType(status) {
    if (['Completed'].includes(status)) return 'completed'
    if (['Cancelled', 'NoShow'].includes(status)) return 'cancelled'
    return 'upcoming'
}

export function getActionLabel(status) {
    if (['Completed'].includes(status)) return 'Rebook'
    if (['Cancelled', 'NoShow'].includes(status)) return 'Cancelled'
    return 'Cancel'
}

export function getStatusStyle(status) {
    if (['Accepted', 'Assigned'].includes(status)) return 'bg-(--text-green) text-white'
    // The unsettled phase — shown as "Booking received" (see STATUS_LABEL_OVERRIDES),
    // golden base + golden ping halo (same animation as the notification dot).
    if (['Requested', 'Broadcasted'].includes(status)) return 'bg-[#f7b032] text-white hopn-status-ping'
    // Driver on the way (EnRoute) and passenger on board (InProgress) are both "under way".
    if (['EnRoute', 'InProgress'].includes(status)) return 'bg-blue-500 text-white'
    if (status === 'Completed') return 'bg-(--text-green) text-white'
    if (status === 'Cancelled' || status === 'NoShow') return 'bg-red-400 text-white'
    if (status === 'PaymentHoldFailed') return 'bg-[#fa8888] text-black'
    return 'bg-gray-400 text-white'
}

// Client-facing overrides for specific statuses, keyed by the raw `status` value — these
// win over the API's status_label (e.g. show "Assigned" as "Driver Assigned").
//
// ⚠️ `Requested` / `Broadcasted` say BOOKING RECEIVED, not the API's "Finding Driver"
// (2026-09-02). Those two statuses are the dispatcher looking for a driver, which is our
// problem and not the passenger's — "Finding Driver" narrates a search and quietly
// implies it might not find one. "Booking received" answers the only question a passenger
// has at that moment: did it go through?
//
// The PILL COLOUR is unchanged: still the amber ping in `getStatusStyle`, because the
// ride genuinely is not settled yet. The words stop worrying the passenger; the colour
// keeps the state honest.
const STATUS_LABEL_OVERRIDES = {
    Requested: 'Booking received',
    Broadcasted: 'Booking received',
    Assigned: 'Driver Assigned',
}

export function getStatusLabel(statusLabel, status) {
    if (STATUS_LABEL_OVERRIDES[status]) return STATUS_LABEL_OVERRIDES[status]
    if (statusLabel) return statusLabel
    if (!status) return status
    return status.replace(/([a-z])([A-Z])/g, '$1 $2')
}

// ── Driver-detail privacy gate ──
//
// The real driver identity (name, photo, rating, plate, vehicle) is a PRIVACY matter
// between the passenger and the driver: the client only wants it disclosed once the
// driver is actually ON THE WAY. Being assigned is NOT enough — an assigned driver who
// hasn't set off yet still hides behind the dummy/blurred card.
//
// `EnRoute` is the moment the driver leaves for the pickup (they're in the car, heading
// over) — that leg is already part of the trip, so it reveals the details just like
// `InProgress` (passenger on board) does. Anything before that stays hidden.
//
// Two distinct questions, deliberately separate:
//  - isDriverAssigned  → do we have a driver at all? (drives the driver-card caption:
//    "Driver details will be shared once your ride starts" flips to "Driver is
//    assigned successfully" the moment one is)
//  - isDriverRevealed  → may we show WHO they are? (assigned AND on the way)
const DRIVER_REVEAL_STATUSES = ['EnRoute', 'InProgress', 'Completed']

export function isDriverAssigned(trip) {
    return !!trip?.driver?.driver_id
}

export function isDriverRevealed(trip) {
    return isDriverAssigned(trip) && DRIVER_REVEAL_STATUSES.includes(trip?.status)
}

// ── Cancellation gate ──
//
// Once the ride is UNDER WAY the passenger can no longer cancel it from the portal — the
// driver is carrying them, so unwinding it is a support matter, not a self-serve one.
//
// Scoped to `InProgress` only, NOT to `EnRoute`: a driver goes en route BEFORE the pickup
// time, and the cancellation ladder deliberately keeps a paid "Late Cancel" tier running
// right up to that pickup time. Blocking at EnRoute would contradict a tier the API still
// reports as active and charges for.
//
// Kept separate from isDriverRevealed's status list on purpose — that one asks "is the
// driver on their way?" (EnRoute counts), this one asks "is the passenger aboard?".
const TRIP_STARTED_STATUSES = ['InProgress']

export const TRIP_STARTED_CANCEL_REASON = 'Your ride has already started. Please contact support if you need help with this trip.'

export function isTripStarted(trip) {
    return TRIP_STARTED_STATUSES.includes(trip?.status)
}

// ── Policy-copy gate ──
//
// From EnRoute onward the cancellation / modification / refund panel stops listing tiers
// and states plainly that none of them apply any more. A driver who has set off is a cost
// already incurred, and a ladder of time bands is the wrong answer to "can I still change
// this?" once the answer is no.
//
// ⚠️ A DIFFERENT LINE FROM `isTripStarted`, AND DELIBERATELY SO — do not merge them.
//   isTripStarted    'InProgress' only. Gates the ACTIONS (the Cancel button), and stops
//                    short of EnRoute because the API keeps a paid Late Cancel tier running
//                    right up to the pickup time; blocking there would refuse a
//                    cancellation the backend still accepts and prices.
//   isTripUnderway   'EnRoute' onward. Gates the COPY only.
//
// ⚠️ SO THE TWO DISAGREE DURING EnRoute, on purpose: the panel says cancellation no longer
// applies while the Cancel control above it is still live. If that reads wrong to the
// business, the fix is to move the ACTION gate to EnRoute as well — one constant, below —
// not to soften this text. They must not be quietly unified in the other direction.
const TRIP_UNDERWAY_STATUSES = ['EnRoute', 'InProgress', 'Completed']

export function isTripUnderway(trip) {
    return TRIP_UNDERWAY_STATUSES.includes(trip?.status)
}

/**
 * THE cancellation gate — `{canCancel, blockedReason}` for any trip.
 *
 * One implementation for every entry point (details page, trips list, edit page). It used
 * to be copy-pasted in three places and simply absent from the list rows, so a started
 * trip still offered a live Cancel button there.
 *
 * The started-check runs BEFORE the policy: a trip under way is blocked whatever the API
 * says, so the button can't stay live on a stale `can_cancel` between polls.
 */
export function tripCancelPolicy(trip) {
    if (isTripStarted(trip)) return {canCancel: false, blockedReason: TRIP_STARTED_CANCEL_REASON}
    const policy = trip?.cancellation_policy
    if (!policy) return {canCancel: true, blockedReason: null}
    // can_cancel can arrive as a real boolean OR a stringified "false"/0 — treat all as blocked.
    const canCancel = ![false, 'false', 0, '0'].includes(policy.can_cancel)
    return {canCancel, blockedReason: policy.blocked_reason || policy.reason || null}
}

// ── Modification allowance ──
//
// The trip's free-modification allowance is exhausted (used >= total). While true the
// whole edit form is read-only, so the entry points into it are disabled too.
//
// `total > 0` guard: a total of 0 means no allowance is configured, NOT "zero edits
// left" — same convention as useEditTrip's gate and the RideEdit submit button.
export function isAtModificationLimit(trip) {
    const policy = trip?.edit_policy
    if (!policy) return false
    const used = Number(policy.modifications_used) || 0
    const total = Number(policy.free_modifications) || 0
    return total > 0 && used >= total
}

// ── Card / payment parsing ──

export function parseCard(pm) {
    if (!pm) return {brand: '', last4: '', expiry: null}
    let meta = pm.metadata
    if (typeof meta === 'string') {
        try { meta = JSON.parse(meta) } catch { meta = {} }
    }
    const brand = (meta?.card?.brand || pm.payment_method_type_label || '').toLowerCase()
    const last4 = meta?.card?.last4 || meta?.last4 || ''
    const expMonth = meta?.card?.exp_month
    const expYear = meta?.card?.exp_year
    const expiry = expMonth && expYear ? `${String(expMonth).padStart(2, '0')}/${String(expYear).slice(-2)}` : null
    return {brand, last4, expiry}
}

// ── Pagination ──

export function getPageNumbers(current, total) {
    if (total <= 5) return Array.from({length: total}, (_, i) => i + 1)
    const pages = [1]
    if (current > 3) pages.push('...')
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i)
    if (current < total - 2) pages.push('...')
    pages.push(total)
    return pages
}

// ── Auth redirect (Laravel-style intended URL) ──

const REDIRECT_KEY = 'hopn_redirect_after_login'

// `/authorize` belongs in this list for the same reason as the rest: it is a place you
// pass THROUGH. Saved as a destination it makes the OTP screen redirect to itself — the
// code verifies, the navigation is a no-op, and it looks like nothing happened.
export function saveRedirectAfterLogin(path) {
    if (!path || path === '/signin' || path === '/signup') return
    if (path.startsWith('/otp-verification') || path.startsWith('/authorize')) return
    sessionStorage.setItem(REDIRECT_KEY, path)
}

export function consumeRedirectAfterLogin() {
    const path = sessionStorage.getItem(REDIRECT_KEY)
    if (path) sessionStorage.removeItem(REDIRECT_KEY)
    return path
}

// ── Misc ──

export function sumQuantity(items) {
    if (!items?.length) return 0
    return items.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0)
}
